import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Archive, BarChart3, Loader2, ListChecks } from "lucide-react";

const CATEGORIES = [
  { code: "procesos", label: "Procesos" },
  { code: "limpieza", label: "Limpieza" },
  { code: "supervision", label: "Supervisión" },
];
const RECURRENCE_LABEL = {
  daily: "Diaria (1 run/día)", daily_per_turn: "Diaria por turno", weekly_by_dow: "Varía por día de semana", on_demand: "Bajo demanda",
};

export default function OpsAdminChecklists() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("procesos");
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/ops/checklist-templates?category_code=${activeCategory}`);
      setTemplates(res.data);
    } catch {
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => { load(); }, [load]);

  const itemCount = (t) => {
    if (t.sections_by_dow) return Object.values(t.sections_by_dow).reduce((a, secs) => a + secs.reduce((b, s) => b + s.items.length, 0), 0);
    return (t.sections || []).reduce((a, s) => a + s.items.length, 0);
  };

  const needsValidationCount = (t) => {
    const allSections = t.sections_by_dow ? Object.values(t.sections_by_dow).flat() : (t.sections || []);
    return allSections.reduce((a, s) => a + s.items.filter((it) => it.needs_validation).length, 0);
  };

  const handleDuplicate = async (id) => {
    try {
      await api.post(`/ops/checklist-templates/${id}/duplicate`);
      toast.success("Plantilla duplicada (inactiva)");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al duplicar");
    }
  };

  const handleArchive = async (id) => {
    try {
      await api.post(`/ops/checklist-templates/${id}/archive`);
      toast.success("Plantilla archivada");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al archivar");
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="ops-admin-checklists-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Checklists — Plantillas</h1>
          <p className="text-sm text-slate-500">Administra categorías, plantillas y reportes de cumplimiento</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/ops/admin/checklists/reportes")}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50 transition-colors"
            data-testid="admin-checklists-reportes-link"
          >
            <BarChart3 className="w-4 h-4" /> Reportes
          </button>
          <button
            onClick={() => navigate("/ops/admin/checklists/nueva")}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#FFDF00] text-sm font-semibold text-slate-900 hover:bg-yellow-300 transition-colors"
            data-testid="admin-checklists-new-btn"
          >
            <Plus className="w-4 h-4" /> Nueva plantilla
          </button>
        </div>
      </div>

      <div className="flex gap-1 border-b border-slate-200">
        {CATEGORIES.map((c) => (
          <button
            key={c.code} onClick={() => setActiveCategory(c.code)} data-testid={`admin-checklists-tab-${c.code}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors -mb-px ${
              activeCategory === c.code ? "border-[#FFDF00] text-slate-900" : "border-transparent text-slate-400 hover:text-slate-600"
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : templates.length === 0 ? (
        <div className="text-center py-16 text-slate-400"><ListChecks className="w-10 h-10 mx-auto mb-2" />Sin plantillas en esta categoría</div>
      ) : (
        <div className="space-y-2" data-testid="admin-checklists-template-list">
          {templates.map((t) => {
            const nv = needsValidationCount(t);
            return (
              <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap" data-testid={`admin-template-row-${t.id}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-slate-900 flex items-center gap-2">
                    {t.name}
                    {!t.active && <span className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">Inactiva</span>}
                    {nv > 0 && <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-600">{nv} por revisar</span>}
                  </div>
                  <div className="text-xs text-slate-500">{RECURRENCE_LABEL[t.recurrence]} · {itemCount(t)} ítems · v{t.version}</div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/ops/admin/checklists/${t.id}`)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`edit-template-btn-${t.id}`}><Pencil className="w-4 h-4 text-slate-600" /></button>
                  <button onClick={() => handleDuplicate(t.id)} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid={`duplicate-template-btn-${t.id}`}><Copy className="w-4 h-4 text-slate-600" /></button>
                  <button onClick={() => handleArchive(t.id)} className="p-2 rounded-lg border border-slate-200 hover:bg-red-50" data-testid={`archive-template-btn-${t.id}`}><Archive className="w-4 h-4 text-red-500" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
